import React from "react";

import { SkillsFacetsCategories } from "shared/modules/Skills/models/SkillsFacetsCategories";
import { ISortArrayConfig, sortArray } from "shared/utils/array";

import { Facet, FacetCategory, FacetLevel, Skill } from "models";

import { IAccessibilityContent } from "pages/MainPage/MainPage";

import { EmptyData } from "components/common/EmptyData";

import { EditPanel } from "../EditPanel";
import { ISkillGridRowEntry, SkillsGrid } from "../SkillsGrid";

export interface ISkillsSectionContent extends IAccessibilityContent {
  skillHeader: string;
  noSkillsPlaceholder: string;
}

const FACET_LEVEL_SETTING: ISortArrayConfig<FacetLevel>[] = [
  {
    expression: (level) => level.position,
    order: "desc",
  },
];

const FACET_CATEGORY_SETTING: ISortArrayConfig<FacetCategory>[] = [
  {
    expression: (level) => level.name,
    order: "asc",
  },
];

export const SKILLS_LIST = [
  SkillsFacetsCategories.AccentsAndDialects.toString(),
  SkillsFacetsCategories.Languages.toString(),
  SkillsFacetsCategories.MusicAndDance.toString(),
  SkillsFacetsCategories.OtherSkills.toString(),
  SkillsFacetsCategories.Performance.toString(),
  SkillsFacetsCategories.Sports.toString(),
  SkillsFacetsCategories.VehicleLicences.toString(),
  SkillsFacetsCategories.Presenting.toString(),
  SkillsFacetsCategories.VoiceOver.toString(),
];

export interface ISkillsSectionProps {
  content: ISkillsSectionContent;
  skills?: Skill[];
  skillFacets: FacetCategory[];
  editSkills: () => void;
  isReadonly: boolean;
}

export class SkillsSection extends React.Component<ISkillsSectionProps> {
  public render() {
    const { editSkills, content, isReadonly } = this.props;

    return (
      <React.Fragment>
        <EditPanel
          header={content.skillHeader}
          editFn={editSkills}
          hideEdit={isReadonly}
          editLinkAltText={content.editLinkAltText}
        />
        {this.renderContent()}
      </React.Fragment>
    );
  }

  private renderContent = () => {
    const { skills } = this.props;

    let content: React.ReactNode = null;

    if (skills) {
      if (skills.length > 0) {
        const skillRows = this.getSkillRows(skills);
        content = <SkillsGrid skills={skillRows} />;
      } else {
        content = this.renderPlaceholder();
      }
    }

    return content;
  };

  private renderPlaceholder = () => (
    <EmptyData theme="light" text={this.props.content.noSkillsPlaceholder} />
  );

  private getSkillRows = (skills: Skill[]) => {
    const { skillFacets } = this.props;
    const skillRows: ISkillGridRowEntry[] = [];

    if (skillFacets.length && skills.length) {
      sortArray(
        skillFacets.filter((facet) => SKILLS_LIST.includes(facet.systemName)),
        FACET_CATEGORY_SETTING
      ).forEach((skillFacet) => {
        const selectedFacets = this.getSelectedSkillFacets(skillFacet);

        const row: ISkillGridRowEntry = {
          categoryName: skillFacet.name,
          categoryId: skillFacet.id,
          rows: [],
        };

        if (skillFacet.facetLevels.length > 0) {
          row.rows = sortArray(skillFacet.facetLevels, FACET_LEVEL_SETTING)
            .map((level) => ({
              levelId: level.id,
              level: level.name,
              skills: this.getSkillListForLevel(
                selectedFacets,
                level.id
              ).sort(),
            }))
            .filter((entry) => entry.skills.length > 0);
        } else if (selectedFacets.length > 0) {
          row.rows.push({
            skills: selectedFacets.map((facet) => facet.name).sort(),
          });
        }

        if (row.rows.length > 0) {
          skillRows.push(row);
        }
      });
    }

    return skillRows;
  };

  private getSkillListForLevel = (selectedFacets: Facet[], levelId: number) => {
    const { skills = [] } = this.props;

    return selectedFacets
      .filter((facet) =>
        skills.some(
          (skill) => skill.facetId === facet.id && skill.levelId === levelId
        )
      )
      .map((facet) => facet.name);
  };

  private getSelectedSkillFacets = (skillFacet: FacetCategory) => {
    const { skills = [] } = this.props;

    return skillFacet.facetGroups
      .reduce<Facet[]>(
        (prevValue, group) => [...prevValue, ...group.facets],
        []
      )
      .filter((facet) => skills.some((skill) => skill.facetId === facet.id));
  };
}
